import { useState } from "react";
import { api, useAction, useApi } from "../../api.ts";
import { fmtMoney } from "../../format.ts";
import {
  Badge, Button, Card, Dialog, EmptyState, ErrorText, Field, IconButton, Input, Loading, LoadError, PageHeader, SearchInput, Stat, Table,
  TBody, Td, Th, THead, Tr, useDebounced, useToast,
} from "../../ui/index.tsx";
import { FormActions, UserCell, When } from "./_ops.tsx";
import type { AdminUserRow, Paged, PaymentsSummary } from "../../../shared/types.ts";

export default function Payments() {
  const summary = useApi<PaymentsSummary>("/admin/payments");
  const [recording, setRecording] = useState(false);
  const s = summary.data;

  return (
    <>
      <PageHeader
        title="Payments"
        description="Manually recorded payments."
        actions={<Button variant="primary" icon="plus" onClick={() => setRecording(true)}>Record payment</Button>}
      />

      {!s ? (
        summary.loading ? <Loading /> : <LoadError error={summary.error} retry={() => void summary.reload()} />
      ) : (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-3">
            <Stat label="Total" value={fmtMoney(s.total_cents)} icon="card" />
            <Stat label="This month" value={fmtMoney(s.month_cents)} icon="chart" />
            <Stat label="Last 30 days" value={fmtMoney(s.last30_cents)} icon="clock" />
          </div>
          <Card title="Payments" padded={false}>
            {s.items.length === 0 ? (
              <EmptyState icon="card" title="No payments recorded" />
            ) : (
              <Table>
                <THead><tr><Th>Date</Th><Th>User</Th><Th>Amount</Th><Th>Description</Th></tr></THead>
                <TBody>
                  {s.items.map((p) => (
                    <Tr key={p.id}>
                      <Td><When at={p.created_at} /></Td>
                      <Td><UserCell id={p.user_id} name={p.username} /></Td>
                      <Td className="whitespace-nowrap">
                        <span className="font-medium tabular-nums">{fmtMoney(p.amount_cents, p.currency)}</span>{" "}
                        <Badge>{p.currency}</Badge>
                      </Td>
                      <Td className="text-muted">{p.description || "—"}</Td>
                    </Tr>
                  ))}
                </TBody>
              </Table>
            )}
          </Card>
        </div>
      )}

      <Dialog open={recording} onClose={() => setRecording(false)} title="Record payment" description="Adds the payment to revenue totals. No money is moved.">
        <PaymentForm onClose={() => setRecording(false)} onSaved={() => { setRecording(false); void summary.reload(); }} />
      </Dialog>
    </>
  );
}

function PaymentForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("GBP");
  const [description, setDescription] = useState("");
  const [user, setUser] = useState<AdminUserRow | null>(null);

  const value = Number(amount);
  const valid = amount.trim() !== "" && Number.isFinite(value) && value > 0 && /^[a-zA-Z]{3}$/.test(currency);

  const save = useAction(async () => {
    await api.post("/admin/payments", {
      amount: value,
      currency: currency.toUpperCase(),
      ...(description.trim() ? { description: description.trim() } : {}),
      ...(user ? { user_id: user.id } : {}),
    });
    toast("Payment recorded", "success");
    onSaved();
  });

  return (
    <form onSubmit={(e) => { e.preventDefault(); void save.run(); }} className="space-y-4">
      <div className="grid grid-cols-[1fr_6rem] gap-3">
        <Field label="Amount"><Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="12.50" required autoFocus /></Field>
        <Field label="Currency"><Input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} maxLength={3} className="font-mono uppercase" required /></Field>
      </div>
      <Field label="Description" hint="Optional"><Input value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
      <UserPicker value={user} onChange={setUser} />
      <ErrorText>{save.error}</ErrorText>
      <FormActions onCancel={onClose} submitLabel="Record" loading={save.pending} disabled={!valid} />
    </form>
  );
}

function UserPicker({ value, onChange }: { value: AdminUserRow | null; onChange: (u: AdminUserRow | null) => void }) {
  const [q, setQ] = useState("");
  const dq = useDebounced(q.trim());
  const results = useApi<Paged<AdminUserRow>>(!value && dq ? `/admin/users?q=${encodeURIComponent(dq)}&limit=8` : null);

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-muted">User <span className="text-faint">(optional)</span></span>
      {value ? (
        <div className="flex items-center justify-between rounded-lg border border-line bg-raised px-3 py-1.5 text-sm">
          <span className="font-medium">{value.username}</span>
          <IconButton icon="x" label="Clear user" onClick={() => onChange(null)} />
        </div>
      ) : (
        <>
          <SearchInput value={q} onChange={setQ} placeholder="Search by username" />
          {dq && (
            <div className="overflow-hidden rounded-lg border border-line">
              {results.error ? (
                <p className="px-3 py-2 text-sm text-bad">{results.error.message}</p>
              ) : !results.data ? (
                <p className="px-3 py-2 text-sm text-muted">Searching…</p>
              ) : results.data.items.length === 0 ? (
                <p className="px-3 py-2 text-sm text-muted">No users match.</p>
              ) : results.data.items.map((u) => (
                <button key={u.id} type="button" onClick={() => { onChange(u); setQ(""); }}
                  className="block w-full border-b border-line px-3 py-2 text-left text-sm last:border-0 hover:bg-hover">
                  {u.username}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
